import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  alpha,
  useTheme,
} from "@mui/material";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { ConfirmDialog } from "./ConfirmDialog";
import { isValidCaseNumber } from "../../features/tn-case-number-input/featureFlag";

export type SignatureMode = "sign" | "rmsCommit" | "coApprove";

export interface SignatureDialogProps {
  open: boolean;
  mode: SignatureMode;
  /**
   * The signer's full display name. In every mode the typed input must match
   * it exactly before the action can be confirmed — this is the typed-name
   * signature gate, shared by officer signing ("sign"/"rmsCommit") and CO
   * supervisory approval ("coApprove").
   */
  signerName: string;
  onClose: () => void;
  /** Receives the case number when `requireCaseNumber` is set (rmsCommit). */
  onConfirm: (caseNumber?: string) => void;
  /** rmsCommit only: the officer must also enter a YY-123456 case number. */
  requireCaseNumber?: boolean;
  /**
   * sign/rmsCommit only (ignored by coApprove): render the accuracy
   * attestation checkbox — legal copy relocated verbatim from the Final
   * Edits page — and require it before the signature can be submitted.
   * Resets on every close: re-attestation is per signature attempt.
   */
  requireAccuracyAttestation?: boolean;
  loading?: boolean;
}

export const ACCURACY_ATTESTATION_LABEL =
  "I confirm that all the information on this page is accurate to my knowledge.";

const SIGN_DESCRIPTION =
  "Are you sure you want to sign the report? This action cannot be undone. " +
  "Once approved, document will be signed and no other changes can be made to the report.";

const RMS_DESCRIPTION =
  "Are you sure you want to submit the report to your Records Management System (RMS)? This action cannot be undone. " +
  "Once submitted, the document will be signed and no other changes can be made to the report. " +
  "It will also be sent to your Records System to be imported and you will receive an event " +
  "number corresponding to the incident created.";

// Strip non-digits, cap at 8 digits (YY + 6), and reinsert the hyphen after YY.
const formatCaseNumberInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

/**
 * The app's single attestation dialog: officer typed-name signature
 * ("sign"), RMS submission with optional case number ("rmsCommit"), and CO
 * supervisory approval ("coApprove"). Consolidates ReportSubmitModal +
 * ApprovalSignatureDialog with a11y fixes (visible labels, Escape/backdrop
 * close, mismatch feedback). Phase 3 (D6) relocated the accuracy
 * attestation checkbox here from the Final Edits page — copy verbatim,
 * gated behind `requireAccuracyAttestation`. All modes share the typed-name
 * signature gate (the entered name must match `signerName`).
 */
export function SignatureDialog({
  open,
  mode,
  signerName,
  onClose,
  onConfirm,
  requireCaseNumber = false,
  requireAccuracyAttestation = false,
  loading,
}: SignatureDialogProps) {
  const theme = useTheme();
  const [enteredName, setEnteredName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [caseNumber, setCaseNumber] = useState("");
  const [caseNumberTouched, setCaseNumberTouched] = useState(false);
  const [accuracyConfirmed, setAccuracyConfirmed] = useState(false);

  // Reset case-number and attestation state when the modal closes so stale
  // input doesn't persist on reopen — the accuracy attestation in particular
  // must be re-given per signature attempt. (The typed name is intentionally
  // not reset — longstanding behavior.)
  useEffect(() => {
    if (!open) {
      setCaseNumber("");
      setCaseNumberTouched(false);
      setNameTouched(false);
      setAccuracyConfirmed(false);
    }
  }, [open]);

  const isCoApprove = mode === "coApprove";
  const nameValid = enteredName === signerName;
  const caseNumberValid = isValidCaseNumber(caseNumber);
  const showCaseNumberError = requireCaseNumber && caseNumberTouched && !caseNumberValid;
  const showNameMismatch = nameTouched && enteredName.length > 0 && !nameValid;
  // Every mode requires the typed-name signature. coApprove ignores the
  // case-number / attestation gates (those are officer-signing only).
  const submitDisabled = isCoApprove
    ? !nameValid
    : !nameValid ||
      (requireCaseNumber && !caseNumberValid) ||
      (requireAccuracyAttestation && !accuracyConfirmed);

  const handleConfirm = () => {
    if (submitDisabled) return;
    onConfirm(requireCaseNumber ? caseNumber : undefined);
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      tone={isCoApprove ? "success" : "primary"}
      icon={isCoApprove ? <VerifiedOutlinedIcon /> : <HistoryEduOutlinedIcon />}
      confirmStartIcon={isCoApprove ? <VerifiedOutlinedIcon /> : undefined}
      maxWidth={480}
      title={isCoApprove ? "Confirm approval" : "Document Approval Confirmation"}
      description={
        isCoApprove ? (
          <>
            By approving, you attest that you have reviewed this report and
            accept it for submission to the Records System.{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              This action is recorded as your supervisory signature.
            </Box>{" "}
            Type your full name below to sign.
          </>
        ) : (
          <>
            {mode === "rmsCommit" ? RMS_DESCRIPTION : SIGN_DESCRIPTION}
            {" To proceed, please type out your name to confirm that you have "}
            reviewed the document. This will serve as your digital signature.
            {" Otherwise, click cancel to return to the pending reports page."}
          </>
        )
      }
      confirmLabel={
        isCoApprove ? "Approve & sign" : mode === "rmsCommit" ? "Submit to Records System" : "Sign Report"
      }
      loading={loading}
      confirmDisabled={submitDisabled}
    >
      <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {!isCoApprove && requireAccuracyAttestation && (
          <FormControlLabel
            control={
              <Checkbox
                checked={accuracyConfirmed}
                disabled={loading}
                onChange={(e) => setAccuracyConfirmed(e.target.checked)}
                sx={{
                  "&.Mui-checked": { color: theme.palette.success.main },
                }}
              />
            }
            label={ACCURACY_ATTESTATION_LABEL}
            slotProps={{
              typography: { variant: "body2", textAlign: "left" },
            }}
            sx={{
              m: 0,
              p: 1,
              borderRadius: 2,
              textAlign: "left",
              bgcolor: accuracyConfirmed
                ? alpha(theme.palette.success.main, 0.08)
                : alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${
                accuracyConfirmed
                  ? alpha(theme.palette.success.main, 0.3)
                  : alpha(theme.palette.divider, 0.1)
              }`,
              transition: "all 0.2s ease-in-out",
            }}
          />
        )}
        {!isCoApprove && requireCaseNumber && (
          <TextField
            type="text"
            id="caseNumber"
            name="caseNumber"
            label="Case Number"
            value={caseNumber}
            fullWidth
            placeholder="YY-123456"
            required
            disabled={loading}
            error={showCaseNumberError}
            helperText={
              showCaseNumberError
                ? "Case number must be 8 digits in YY-123456 format (e.g. 26-000123)."
                : "Enter the 8-digit case number for the incident (the hyphen is added automatically)."
            }
            inputProps={{ inputMode: "numeric", autoComplete: "off", maxLength: 9 }}
            onChange={(e) => setCaseNumber(formatCaseNumberInput(e.target.value))}
            onBlur={() => setCaseNumberTouched(true)}
          />
        )}
        <TextField
          type="text"
          id="signerName"
          name="signerName"
          label="Full name"
          value={enteredName}
          fullWidth
          required
          autoFocus={!requireCaseNumber}
          disabled={loading}
          error={showNameMismatch}
          helperText={
            showNameMismatch
              ? `Name must match "${signerName}" exactly.`
              : `Sign as "${signerName}"`
          }
          inputProps={{ autoComplete: "off" }}
          onChange={(e) => setEnteredName(e.target.value)}
          onBlur={() => setNameTouched(true)}
        />
      </Box>
    </ConfirmDialog>
  );
}
